// src/api/axiosClient.js
// -----------------------------------------------------------------------------
// Single shared Axios instance used by EVERY service file in src/api/.
// Keeping one instance here means:
//   1. Base URL is defined in ONE place (reads from .env -> VITE_API_BASE_URL)
//   2. The JWT auth token is attached to every request automatically
//   3. 401 (token expired) responses are handled in ONE place: the refresh
//      token is used once to get a new access token, otherwise auto logout
// Every *Service.js file in this folder should import "axiosClient" instead of
// importing axios directly, and call axiosClient.get/post/put/delete(...).
// -----------------------------------------------------------------------------
import axios from 'axios';
import loadingStore from './loadingStore';

// Base URL of the AFix C# Web API. Falls back to "/api" (proxied by Vite in dev).
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

const axiosClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30s timeout for slow requests
});

// ---------------------------------------------------------------------------
// REQUEST INTERCEPTOR
// Attaches the JWT access token (stored after login) to every outgoing
// request, matching the backend's "Secure login using JWT authentication"
// requirement (Original Requirement #2).
// ---------------------------------------------------------------------------
axiosClient.interceptors.request.use(
  (config) => {
    // Only trigger the blocking global loader if explicitly requested via showGlobalLoader
    if (config.showGlobalLoader && !config.skipGlobalLoader) {
      const msg = typeof config.showGlobalLoader === 'string' ? config.showGlobalLoader : 'Processing request...';
      loadingStore.increment(msg);
    }
    const token = localStorage.getItem('afix_access_token');
    if (token && token !== 'undefined' && token !== 'null') {
      config.headers.Authorization = `Bearer ${token}`;
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    if (error?.config?.showGlobalLoader && !error.config?.skipGlobalLoader) {
      loadingStore.decrement();
    }
    return Promise.reject(error);
  }
);

// ---------------------------------------------------------------------------
// RESPONSE INTERCEPTOR
// - On 401 Unauthorized: the access token expired -> exchange the refresh
//   token for a new one (POST /api/Auth/refresh-token) and retry the request
//   once. If that fails too, clear storage & redirect to login.
// - On any other error: just pass it along so the calling component/page can
//   show a toast / inline error message.
// ---------------------------------------------------------------------------
const clearSessionAndGoToLogin = () => {
  localStorage.removeItem('afix_access_token');
  localStorage.removeItem('afix_refresh_token');
  localStorage.removeItem('afix_user');
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

// One shared refresh call, so several requests failing with 401 at the same
// time trigger a single refresh instead of racing each other.
let refreshPromise = null;

const refreshAccessToken = () => {
  if (!refreshPromise) {
    const refreshToken = localStorage.getItem('afix_refresh_token');
    refreshPromise = (refreshToken
      ? axios.post(`${API_BASE_URL}/Auth/refresh-token`, { refreshToken })
      : Promise.reject(new Error('No refresh token'))
    )
      .then((res) => {
        const data = res.data || {};
        const newToken = data.token || data.Token;
        const newRefreshToken = data.refreshToken || data.RefreshToken;
        if (!newToken || !newRefreshToken) throw new Error('Refresh failed');
        localStorage.setItem('afix_access_token', newToken);
        localStorage.setItem('afix_refresh_token', newRefreshToken);
        return newToken;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

axiosClient.interceptors.response.use(
  (response) => {
    if (response.config?.showGlobalLoader && !response.config?.skipGlobalLoader) {
      loadingStore.decrement();
    }
    return response;
  },
  (error) => {
    if (error?.config?.showGlobalLoader && !error.config?.skipGlobalLoader) {
      loadingStore.decrement();
    }
    const originalRequest = error?.config;
    if (error?.response?.status === 401 && originalRequest) {
      const token = localStorage.getItem('afix_access_token');
      const isAuthCall = /\/auth\/(login|refresh-token|logout)/i.test(originalRequest.url || '');
      if (token && token !== 'demo-token' && !isAuthCall) {
        if (!originalRequest._retried) {
          originalRequest._retried = true;
          return refreshAccessToken()
            .then((newToken) => {
              originalRequest.headers = originalRequest.headers || {};
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return axiosClient(originalRequest);
            })
            .catch(() => {
              console.warn('HTTP 401 Unauthorized: session expired. Logging out...');
              clearSessionAndGoToLogin();
              return Promise.reject(error);
            });
        }
        console.warn('HTTP 401 Unauthorized: Token expired or invalid. Logging out...');
        clearSessionAndGoToLogin();
      }
      return Promise.reject(error);
    }
    return Promise.reject(error);
  }
);

export default axiosClient;
